import {
  LineupEntry,
  LineupPair,
  LineupPositions,
  PlayerPublic,
  SideType,
  TeamDB,
} from "@aura-frontend/omnibus-az-client";

import { UploadedPlayer } from "@/utils/uploadedPlayers";

import { TeamSide } from "../constants/teams";
import {
  LineUpPlayer,
  OmnibusPlayerRecord,
  TaggedPlayer,
} from "../types/gameFull";
import { splitPlayerName } from "../utils/playerEdit";

/**
 * Mapping between the game service shapes the UI renders (LineUpPlayer) and
 * the omnibus shapes we write with (LineupEntry, PlayerPublic).
 */

/** Ids for players added in the UI that don't exist in omnibus yet. */
export const LOCAL_PLAYER_ID_PREFIX = "local-";

export const isLocalPlayer = (player: Pick<LineUpPlayer, "playerId">) =>
  player.playerId.startsWith(LOCAL_PLAYER_ID_PREFIX);

const SIDE_TYPES: SideType[] = ["left", "right", "switch"];

export const toSideType = (side?: string | null): SideType | undefined => {
  const value = side?.toLowerCase();
  return SIDE_TYPES.find((s) => s === value);
};

const toJerseyNumber = (jerseyNumber?: string | number | null) => {
  if (jerseyNumber == null || jerseyNumber === "") return undefined;
  const value = parseInt(String(jerseyNumber), 10);
  return Number.isNaN(value) ? undefined : value;
};

/** The player's own jersey number, apart from any roster's. */
export const getPlayerJerseyNumber = (player: PlayerPublic) =>
  // Served but missing from the generated type (see CreateTeamRosterDialog)
  toJerseyNumber(
    (player as unknown as Record<string, unknown>).jersey_number as
      | string
      | number
      | null,
  );

/**
 * Omnibus lineup position: the batting order slot, else the defensive
 * position for slotless pitchers/catchers (the game service's "P"/"C").
 */
export const toLineupPosition = (
  player: Pick<LineUpPlayer, "battingOrder" | "position">,
): number | LineupPositions | undefined => {
  if (player.battingOrder != null) return player.battingOrder;
  switch (player.position?.toUpperCase()) {
    case "P":
      return "pitcher";
    case "C":
      return "catcher";
    default:
      return undefined;
  }
};

/**
 * Both teams' current lineups as omnibus stores them (GET /game/active), with
 * every substitution applied.
 */
export type StoredLineups = Record<TeamSide, LineupEntry[]>;

const isFielderEntry = (entry: LineupEntry) =>
  entry.position === "pitcher" || entry.position === "catcher";

/**
 * Both teams' lineups to save: the batting order by slot, then each team's
 * pitcher and catcher exactly as omnibus stores them. The game service's P/C
 * rows can't be written back: a pitcher who also bats shares one row with
 * their slot, and a pitcher subbed out keeps their row, so they'd drop or
 * restore pitchers and catchers. Only a jersey number edited here replaces
 * the stored one.
 */
export const toLineupPair = (
  lineUps: Record<TeamSide, LineUpPlayer[]>,
  stored: StoredLineups,
): Required<LineupPair> => {
  const entries = (side: TeamSide) => {
    const fielders = stored[side].filter(isFielderEntry).map((entry) => {
      const jerseyNumber = toJerseyNumber(
        lineUps[side].find((p) => p.playerId === entry.externalId)
          ?.jerseyNumber,
      );
      return jerseyNumber === undefined ? entry : { ...entry, jerseyNumber };
    });
    const battingOrder = lineUps[side].flatMap((player) =>
      player.battingOrder == null
        ? []
        : [toLineupEntry(player, player.battingOrder)],
    );
    return [...fielders, ...battingOrder];
  };
  return {
    awayLineup: entries("away"),
    homeLineup: entries("home"),
  };
};

/**
 * A substitution's outgoing player as omnibus stores them, which it requires
 * field for field: whoever holds `rebuilt`'s position (a team has one
 * pitcher, one catcher and one player per slot). The game service's copy can
 * differ, or name a pitcher omnibus already subbed out. Falls back to
 * `rebuilt` when nobody holds it.
 */
export const findStoredLineupEntry = (
  stored: LineupEntry[],
  rebuilt: LineupEntry,
): LineupEntry =>
  stored.find((entry) => entry.position === rebuilt.position) ?? rebuilt;

/** A pitch's tagged hitter/pitcher as an omnibus lineup entry. */
export const taggedPlayerToLineupEntry = (
  player: TaggedPlayer,
  position: number | LineupPositions,
): LineupEntry => ({
  externalId: player.externalId,
  playerName: player.name,
  hitSide: toSideType(player.hitSide),
  pitchSide: toSideType(player.pitchSide),
  jerseyNumber: toJerseyNumber(player.jerseyNumber),
  position,
});

/**
 * The name to write for a player: "Last, First", as omnibus and the event
 * form store names, for a player taken from a roster (`name` is for display).
 * A player from the game keeps the name the lineup stores, since omnibus only
 * substitutes out a player matching their lineup entry exactly.
 */
export const toLineupPlayerName = (player: LineUpPlayer) => {
  if (!player.omnibus) return player.name;
  const { firstName, lastName } = player.omnibus;
  return firstName ? `${lastName}, ${firstName}` : lastName || player.name;
};

export const toLineupEntry = (
  player: LineUpPlayer,
  position: number | LineupPositions,
): LineupEntry =>
  taggedPlayerToLineupEntry(
    {
      externalId: player.playerId,
      name: toLineupPlayerName(player),
      jerseyNumber: player.jerseyNumber,
      hitSide: player.hitSide,
      pitchSide: player.pitchSide,
    },
    position,
  );

/**
 * Omnibus stores names as "Last, First" (a name without a comma is kept
 * whole as the last name, as omnibus does).
 */
export const parseKnownName = (knownName: string) => {
  const [last = "", ...rest] = knownName.split(", ");
  return { first: rest.join(", ").trim(), last: last.trim() };
};

const toDisplayName = ({ first, last }: { first: string; last: string }) =>
  [first, last].filter(Boolean).join(" ") || "Unknown";

/**
 * A lineup player from an omnibus player. `jerseyNumber` is the roster
 * member's number when given; the player's own number is kept in `omnibus`.
 */
export const playerPublicToLineUpPlayer = (
  player: PlayerPublic,
  jerseyNumber?: number | null,
): LineUpPlayer => {
  const name = parseKnownName(player.known_names[0] ?? "");
  const ownJersey = getPlayerJerseyNumber(player);
  const displayJersey = jerseyNumber ?? ownJersey;
  return {
    playerId: player.external_ids[0] ?? "",
    name: toDisplayName(name),
    jerseyNumber: displayJersey != null ? String(displayJersey) : undefined,
    hitSide: player.hit_side,
    pitchSide: player.pitch_side,
    omnibus: {
      firstName: name.first,
      lastName: name.last,
      bats: player.hit_side,
      throws: player.pitch_side,
      jerseyNumber: ownJersey,
    },
  };
};

/**
 * A lineup player for a player omnibus just created (Create Players / Bulk
 * Upload). Their record is kept as created, so the roster add links them
 * without changing it.
 */
export const uploadedPlayerToLineUpPlayer = (
  player: UploadedPlayer,
): LineUpPlayer => {
  const bats = player.bats || undefined;
  const throws = player.throws || undefined;
  return {
    playerId: player.externalId,
    name: toDisplayName({ first: player.firstName, last: player.lastName }),
    jerseyNumber:
      player.jerseyNumber !== undefined
        ? String(player.jerseyNumber)
        : undefined,
    hitSide: bats,
    pitchSide: throws,
    omnibus: {
      firstName: player.firstName,
      lastName: player.lastName,
      bats,
      throws,
      jerseyNumber: player.jerseyNumber,
    },
  };
};

/**
 * Body entry for POST /rosters/{id}/players. Existing players are linked by
 * `external_id`; new players are created from their details.
 *
 * Omnibus overwrites a linked player's name, jersey and handedness with the
 * entry (and puts the name first in their known names), so a player loaded
 * from omnibus is sent exactly as their record has them. Bats and throws are
 * required: "right" only fills in for a player with no handedness at all.
 * The generated type lacks `external_id`/`jersey_number`, which the API
 * accepts (same as CreateTeamRosterDialog and TeamLineupPanel).
 */
export const toRosterPlayerInfo = (player: LineUpPlayer) => {
  const record: OmnibusPlayerRecord = player.omnibus ?? {
    ...splitPlayerName(player.name),
    bats: player.hitSide,
    throws: player.pitchSide,
    jerseyNumber: toJerseyNumber(player.jerseyNumber),
  };
  return {
    first_name: record.firstName,
    last_name: record.lastName,
    bats: toSideType(record.bats) ?? "right",
    throws: toSideType(record.throws) ?? "right",
    ...(isLocalPlayer(player) ? {} : { external_id: player.playerId }),
    ...(record.jerseyNumber !== undefined
      ? { jersey_number: record.jerseyNumber }
      : {}),
  };
};

// Roster members come back camelCase or snake_case depending on the record
// (see ChangeJerseyNumberModal)
export type RosterMember = NonNullable<TeamDB["unofficial_members"]>[number];

export const getMemberExternalId = (member: RosterMember) => {
  const record = member as unknown as Record<string, unknown>;
  return (record.externalId ?? record.external_id) as string | undefined;
};

export const getMemberJerseyNumber = (member: RosterMember) => {
  const record = member as unknown as Record<string, unknown>;
  const value = record.jerseyNumber ?? record.jersey_number;
  return value != null && value !== "" ? Number(value) : null;
};

/**
 * Matches the players sent to POST /rosters/{id}/players to the members it
 * added. Omnibus appends one member per player, in the order sent, so they
 * are the roster's last members; new (local) players take their member's
 * external id. Throws if the members don't line up with what was sent.
 */
export const pairAddedRosterMembers = (
  sent: LineUpPlayer[],
  members: RosterMember[],
): LineUpPlayer[] => {
  if (members.length < sent.length) {
    throw new Error("The roster didn't come back with the added players");
  }
  const added = members.slice(members.length - sent.length);
  return sent.map((player, index) => {
    const externalId = getMemberExternalId(added[index]);
    if (!externalId) {
      throw new Error(`Added ${player.name} to the roster without a player id`);
    }
    if (isLocalPlayer(player)) return { ...player, playerId: externalId };
    if (externalId !== player.playerId) {
      throw new Error("The roster came back with unexpected players");
    }
    return player;
  });
};
